import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/ui/InputField';
import SegmentedTabs from '../../../../components/SegmentedTabs';
import AssetDetailSection from '../../../assets/components/AssetDetailSection';
import { useTenantCapabilities } from '../../../../hooks/useTenantCapabilities';
import { type StationCreationForm, bookableObjectCount } from '../stationCreationForm';

/**
 * Use cases where a unit has a manufacturer's serial worth recording: a drill,
 * a board. A storage compartment has none — it is a hole in a cabinet — so
 * offering the field there was noise the operator had to read past
 * (PO, staging 2026-08-31).
 */
const SERIAL_USE_CASES = new Set(['rental', 'sup', 'keys', 'p2p']);

interface ObjectsStepProps {
    form: StationCreationForm;
    update: <K extends keyof StationCreationForm>(key: K, value: StationCreationForm[K]) => void;
}

/**
 * Step 3 — the objects (= `asset[]`) that sit at the station's positions,
 * bulk-created. Count is derived from the station (positions − held-back).
 * See `docs/plans/object-and-station-creation-flow.md` §2/§3.
 *
 * The toggle names ONLY the naming. Per-unit facts — volume, note, and where it
 * applies a serial — are offered in both modes, because they have nothing to do
 * with how the object got its name: a cabinet of auto-named compartments still
 * has different volumes per Fach, and the first build hid those fields behind
 * "name each one individually" (PO, staging 2026-08-31).
 */
const ObjectsStep = ({ form, update }: ObjectsStepProps) => {
    const { t } = useTranslation();
    const count = bookableObjectCount(form) ?? 0;
    const { services } = useTenantCapabilities();

    // When capabilities are unresolved the field is shown rather than hidden:
    // an absent optional field cannot be recovered by the operator, a spare one
    // can be ignored (same stand-down as the product filter).
    const showSerial = services.length === 0
        || services.some((service) => SERIAL_USE_CASES.has(service.toLowerCase()));

    const isAuto = form.objectNaming === 'auto';
    const prefix = form.objectNamePrefix.trim();

    const setItem = (
        key: 'objectNames' | 'objectSerials' | 'objectVolumes' | 'objectNotes',
        index: number,
        value: string,
    ) => {
        const next = [...form[key]];
        next[index] = value;
        update(key, next);
    };

    /** What the object will be called — mirrors `buildCreateObjectsRequest`. */
    const autoName = (index: number) => (prefix ? `${prefix} ${index + 1}` : `${index + 1}`);

    return (
        <AssetDetailSection
            title={t('station_create.objects.title')}
            subtitle={t('station_create.objects.subtitle', { count })}
        >
            <div className={'flex flex-col gap-5'}>
                <SegmentedTabs<'auto' | 'manual'>
                    items={[
                        { key: 'auto', label: t('station_create.objects.naming_auto') },
                        { key: 'manual', label: t('station_create.objects.naming_manual') },
                    ]}
                    value={form.objectNaming}
                    onChange={(key) => update('objectNaming', key)}
                />

                {isAuto && (
                    <div className={'flex flex-col gap-3'}>
                        <InputField
                            labelAbove
                            label={t('station_create.objects.prefix_label')}
                            placeholder={t('station_create.objects.prefix_placeholder')}
                            value={form.objectNamePrefix}
                            onChange={(v) => update('objectNamePrefix', v)}
                        />
                        {prefix && count > 0 && (
                            <p className={'text-sm text-foreground/60'}>
                                {count === 1
                                    ? t('station_create.objects.preview_single', {
                                        name: autoName(0),
                                    })
                                    : t('station_create.objects.preview', {
                                        first: autoName(0),
                                        last: autoName(count - 1),
                                    })}
                            </p>
                        )}
                    </div>
                )}

                {/* Auto-naming means the objects are alike, so they are
                    described ONCE rather than in N identical cards. Ten
                    compartments used to render ten cards the operator had to
                    scroll past — "automatisch benennen" saved the names and
                    nothing else. Per-unit facts stay in manual mode, which is
                    what it is for. */}
                {isAuto ? (
                    <div className={'flex flex-col gap-3'}>
                        <p className={'text-sm text-foreground/60'}>
                            {t('station_create.objects.shared_details_hint', { count })}
                        </p>
                        <div className={'grid grid-cols-1 gap-3 sm:grid-cols-2'}>
                            <InputField
                                labelAbove
                                type={'number'}
                                integerOnly
                                min={0}
                                label={t('station_create.objects.shared_volume_label')}
                                placeholder={t('station_create.objects.optional')}
                                value={form.sharedObjectVolume}
                                onChange={(v) => update('sharedObjectVolume', v)}
                            />
                            <InputField
                                labelAbove
                                label={t('station_create.objects.shared_note_label')}
                                placeholder={t('station_create.objects.optional')}
                                value={form.sharedObjectNote}
                                onChange={(v) => update('sharedObjectNote', v)}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={'flex flex-col gap-3'}>
                        <p className={'text-sm text-foreground/60'}>
                            {t('station_create.objects.per_object_hint')}
                        </p>
                        {Array.from({ length: count }, (_, i) => (
                            <div
                                key={i}
                                className={'rounded-[5px] border border-foreground/10 p-3'}
                            >
                                <p className={'mb-2 text-xs font-medium text-foreground/60'}>
                                    {t('station_create.objects.object_label', { index: i + 1 })}
                                </p>
                                <div className={'grid grid-cols-1 gap-3 sm:grid-cols-2'}>
                                    <InputField
                                        labelAbove
                                        label={t('station_create.objects.object_name_label', { index: i + 1 })}
                                        value={form.objectNames[i] ?? ''}
                                        onChange={(v) => setItem('objectNames', i, v)}
                                    />
                                    {showSerial && (
                                        <InputField
                                            labelAbove
                                            label={t('station_create.objects.serial_label')}
                                            placeholder={t('station_create.objects.optional')}
                                            value={form.objectSerials[i] ?? ''}
                                            onChange={(v) => setItem('objectSerials', i, v)}
                                        />
                                    )}
                                    <InputField
                                        labelAbove
                                        type={'number'}
                                        integerOnly
                                        min={0}
                                        label={t('station_create.objects.volume_label')}
                                        placeholder={t('station_create.objects.optional')}
                                        value={form.objectVolumes[i] ?? ''}
                                        onChange={(v) => setItem('objectVolumes', i, v)}
                                    />
                                    <InputField
                                        labelAbove
                                        label={t('station_create.objects.note_label')}
                                        placeholder={t('station_create.objects.optional')}
                                        value={form.objectNotes[i] ?? ''}
                                        onChange={(v) => setItem('objectNotes', i, v)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AssetDetailSection>
    );
};

export default ObjectsStep;
